import { IntlState } from "react-intl-redux";
import { CheckoutPaymentData, FormCheckoutData } from "checkout/ts/components/checkout/FormCheckout";
import { FormDataCard } from "checkout/ts/components/flows/card";
import { CardData } from "checkout/ts/components/flows/card/FormData";
import { formatPhoneNumber } from "checkout/ts/components/flows/card/utils/phoneNumber";
import { FormDataKonbini } from "checkout/ts/components/flows/konbini";
import { TokenError } from "checkout/ts/errors/TokenError";
import { LOCALE_LABELS } from "checkout/ts/locale/labels";
import { PatchedTokenCardData, ThreeDsStatus } from "checkout/ts/redux/sdk/resources/TransactionTokens";
import { sdk } from "checkout/ts/redux/sdk/SDK";
import { cardBrandAPIName } from "checkout/ts/utils/cards";
import { getCheckoutParams } from "checkout/ts/utils/checkout-params";
import { getMessage } from "checkout/ts/utils/intl";
import { UnivaMetadata } from "checkout/ts/utils/metadata";
import { ParametersError } from "common/errors/ParametersError";
import { ResourceType } from "common/Messages";
import { CheckoutParams, CheckoutType, ThreeDsMode } from "common/types";
import { needsCvv } from "common/validation/card";
import { isEqual, omit } from "lodash";
import Payment from "payment";
import {
    CvvAuthorizedStatus,
    PaymentType,
    TransactionTokenBankTransferCreateData,
    TransactionTokenCardData,
    TransactionTokenCardDataItem,
    TransactionTokenConvenienceData,
    TransactionTokenCreateParams,
    TransactionTokenItem,
    TransactionTokenOnlineData,
    TransactionTokenType,
} from "univapay-node";

import { ApplicationParams } from "../models/application";
import { PatchedCheckoutInfo } from "../models/configuration";
import { PatchProductItem } from "../models/product";
import { PatchedTransactionTokenUpdateParams } from "../models/tokens";
import { UserDataStateShape } from "../models/user-data";
import { rateLimit } from "../rate-limiter";
import { StateShape } from "../store";

import { concatPhoneNumber, getIntlMessage, parsePhone } from "./intl";

type PatchedTransactionTokenConvenienceData = TransactionTokenConvenienceData & {
    expirationTimeShift?: string;
    expirationPeriod?: string;
};

type PatchedTransactionTokenBankTransferData = TransactionTokenBankTransferCreateData & {
    phoneNumber?: string;
    expirationTimeShift?: string;
    expirationPeriod?: string;
};

export type PatchedThreeDsTransactionToken = TransactionTokenItem & {
    data?: { threeDs?: { enabled: boolean; status: ThreeDsStatus } };
};

export const needsCvvAuth = (data: FormCheckoutData, state: StateShape) => {
    const checkoutParams = getCheckoutParams(
        state.application.params,
        state.product.products,
        state.configuration.data
    );
    const isCvvShown = needsCvv(state);

    if (!state.application.params.params.cvvAuthorize) {
        // cvv authorized parameter forced the authorization
        return false;
    }

    if (state.checkout.paymentType !== PaymentType.CARD || !isCvvShown) {
        // Only accepts card payment with CVV data
        return false;
    }

    const cardNumber = (data?.data as TransactionTokenCardData)?.cardNumber;
    const cardBrand = cardNumber ? cardBrandAPIName(Payment.fns.cardType(cardNumber)) : undefined;
    const isBrandCvvAuthCompatible =
        cardBrand &&
        state.configuration.data.supportedBrands.some(({ brand, cvvAuth }) => cardBrand === brand && !!cvvAuth);

    return (
        isBrandCvvAuthCompatible &&
        (checkoutParams.checkoutType === CheckoutType.TOKEN || hasDeferredCharges(checkoutParams))
    );
};

export const isChargeInstallments = (installmentCycles: "revolving" | number) =>
    installmentCycles === "revolving" || installmentCycles > 1;

export const hasDeferredCharges = (params: ReturnType<typeof getCheckoutParams>) =>
    params.isSubscription ? params.initialAmount === 0 : false;

const getTokenParams = (
    data: FormCheckoutData,
    applicationParams: ApplicationParams,
    paymentType: PaymentType,
    userData: UserDataStateShape,
    checkoutInfo: PatchedCheckoutInfo,
    products: PatchProductItem[],
    withCvvAuth: boolean,
    intl: IntlState
): TransactionTokenCreateParams => {
    const {
        tokenType: type,
        bankTransferExpirationPeriod,
        bankTransferExpirationTimeShift,
        convenienceStoreExpirationPeriod,
        convenienceStoreExpirationTimeShift,
        univapayCustomerId,
        currency,
        params: {
            usageLimit,
            metadata = {},
            metadataToken = {},
            confirmationRequired,
            phoneNumber: paramsPhoneNumber,
            univapayReferenceId,
            hideRecurringCheckbox,
            threeDsMode,
            threeDsRedirect,
            threeDsSkipToken,
        },
    } = getCheckoutParams(applicationParams, products, checkoutInfo);

    const saveCard =
        data.data?.accept ??
        (hideRecurringCheckbox ? !!univapayCustomerId || type === TransactionTokenType.RECURRING : false); // This parameter is only available to card data

    const tokenMetadata = {
        // Common information metadata
        [UnivaMetadata.PHONE_NUMBER]:
            concatPhoneNumber(userData.phoneNumber || parsePhone(paramsPhoneNumber)) || undefined,
        [UnivaMetadata.NAME]: userData.name || undefined,
        [UnivaMetadata.NAME_KANA]: userData.nameKana || undefined,
        [UnivaMetadata.ADDRESS_CITY]: userData.city || undefined,
        [UnivaMetadata.ADDRESS_COUNTRY]: userData.country || undefined,
        [UnivaMetadata.ADDRESS_ZIP]: userData.zip || undefined,
        [UnivaMetadata.ADDRESS_STATE]: userData.state || undefined,
        [UnivaMetadata.ADDRESS_LINE1]: userData.line1 || undefined,
        [UnivaMetadata.ADDRESS_LINE2]: userData.line2 || undefined,
        [UnivaMetadata.CUSTOMER_ID]: univapayCustomerId,
        [UnivaMetadata.LEGACY_CUSTOMER_ID]: univapayCustomerId,
        [UnivaMetadata.REFERENCE_ID]: univapayReferenceId,
        [UnivaMetadata.PRODUCT_NAMES]: products
            ?.map((product) => product.name)
            ?.join(getIntlMessage(LOCALE_LABELS.COMMON_COMMA, intl)),
        [UnivaMetadata.PRODUCT_QUANTITIES]: products
            ?.map((product) => product.quantity)
            ?.join(getIntlMessage(LOCALE_LABELS.COMMON_COMMA, intl)),

        ...metadata,
        ...metadataToken,
        ...userData.customFields,
        ...(products || []).reduce((acc, product) => ({ ...acc, ...product.metadata }), {}),
    };

    const tokenType = (() => {
        if (withCvvAuth || saveCard) {
            // Saving a card requires a recurring token independently on the type
            return TransactionTokenType.RECURRING;
        }

        // For one time with installment switch to a subscription as several payments are needed
        const isOneTimeWithInstallment =
            isChargeInstallments(data.installmentCycles) && type === TransactionTokenType.ONE_TIME;
        return isOneTimeWithInstallment ? TransactionTokenType.SUBSCRIPTION : type;
    })();

    const commonParams = {
        type: tokenType,
        paymentType: data.paymentType || paymentType,
        email: userData.email || ("email" in data ? (data as FormDataCard | FormDataKonbini).email : undefined),
        metadata: tokenMetadata,
        useConfirmation: confirmationRequired,
    };

    const tokenThreeDs = (() => {
        const redirectEndpoint =
            threeDsRedirect ??
            `${window.origin}/redirect/index.html?threeDsRedirect=true&${UnivaMetadata.LOCALE}=${intl.locale}&appId=${sdk.api.jwtRaw}&apiBase=${sdk.api.endpoint}`;

        if (threeDsMode === ThreeDsMode.SKIP || threeDsSkipToken) {
            return {
                enabled: false,
                redirectEndpoint,
            };
        } else if (tokenType === TransactionTokenType.RECURRING) {
            return {
                enabled: threeDsMode === ThreeDsMode.FORCE ? true : undefined,
                redirectEndpoint,
            };
        }

        return undefined;
    })();

    switch (data.paymentType || paymentType) {
        case PaymentType.CARD:
        case PaymentType.APPLE_PAY: {
            const params = {
                ...commonParams,
                usageLimit,
                data: {
                    line1: userData.line1 || undefined,
                    line2: userData.line2 || undefined,
                    state: userData.state || undefined,
                    city: userData.city || undefined,
                    country: userData.country || undefined,
                    zip: userData.zip || undefined,
                    threeDs: tokenThreeDs,
                    ...data.data,
                    phoneNumber: formatPhoneNumber(userData.phoneNumber),
                    cvvAuthorize: {
                        enabled: withCvvAuth,
                        currency,
                    },
                } as CardData,
            };

            return tokenType === TransactionTokenType.RECURRING ? params : omit(params, "usageLimit");
        }

        case PaymentType.QR_SCAN:
        case PaymentType.KONBINI:
            return {
                ...commonParams,
                data: {
                    ...data.data,
                    ...formatPhoneNumber(userData.phoneNumber),
                    expirationPeriod: convenienceStoreExpirationPeriod,
                    expirationTimeShift: convenienceStoreExpirationTimeShift,
                } as PatchedTransactionTokenConvenienceData,
            };

        case PaymentType.PAIDY:
            return {
                ...commonParams,
                data: {
                    ...data.data,
                    ...formatPhoneNumber(userData.phoneNumber),
                } as TransactionTokenCardData,
            };

        case PaymentType.ONLINE:
            return {
                ...commonParams,
                data: { brand: null, ...data.data } as TransactionTokenOnlineData,
            };

        case PaymentType.BANK_TRANSFER:
            return {
                ...commonParams,
                data: ({
                    ...data.data,
                    ...formatPhoneNumber(userData.phoneNumber),
                    expirationPeriod: bankTransferExpirationPeriod,
                    expirationTimeShift: bankTransferExpirationTimeShift,
                } as unknown) as PatchedTransactionTokenBankTransferData,
            };

        default:
            throw new ParametersError(LOCALE_LABELS.ERRORS_ALERTS_UNKNOWN_PAYMENT_TYPE);
    }
};

export const createTransactionToken = async (data: FormCheckoutData, state: StateShape) => {
    const cvvAuth = needsCvvAuth(data, state);

    const tokenParams = getTokenParams(
        data,
        state.application.params,
        state.checkout.paymentType,
        state.userData,
        state.configuration.data,
        state.product.products,
        cvvAuth,
        state.intl
    );

    // Create a new token when no prior token was selected
    const newToken = (await rateLimit(() => sdk.transactionTokens.create(tokenParams))) as TransactionTokenItem;

    const cvvAuthStatus = newToken.data?.cvvAuthorize?.status;
    const shouldPollCvvAuth = cvvAuthStatus ? cvvAuthStatus === CvvAuthorizedStatus.PENDING : cvvAuth;
    const authToken = shouldPollCvvAuth
        ? await sdk.patchedTransactionTokens.pollCvvAuth(newToken.storeId, newToken.id)
        : newToken;

    const validToken = await (async () => {
        if (
            authToken.paymentType !== PaymentType.CARD || // 3DS is only for card
            authToken.type !== TransactionTokenType.RECURRING || // 3DS is on the charge when the token is not recurring
            !(authToken.data as PatchedTokenCardData)?.threeDs?.enabled // API computes wether the 3DS authorization is needed on the token
        ) {
            return authToken;
        }

        state.application.connector.emitter.emit("checkout:three-ds-authorization", {
            resourceType: ResourceType.TRANSACTION_TOKEN,
            response: authToken,
            tokenId: authToken?.id,
        });

        let token: PatchedThreeDsTransactionToken;
        try {
            await sdk.patchedTransactionTokens.pollPendingThreeDsToken(authToken.storeId, authToken.id);
            token = await sdk.patchedTransactionTokens.pollThreeDs(
                authToken.storeId,
                authToken.id,
                state.intl.locale,
                state.application.connector
            );

            if (token.data?.threeDs?.status !== ThreeDsStatus.SUCCESSFUL) {
                throw new TokenError(
                    token.id,
                    undefined,
                    token.storeId,
                    getMessage(LOCALE_LABELS.ERRORS_ALERTS_THREE_DS_FAILED),
                    LOCALE_LABELS.ERRORS_ALERTS_THREE_DS_FAILED
                );
            }

            state.application.connector.emitter.emit("checkout:three-ds-authorization-success", {
                resourceType: ResourceType.TRANSACTION_TOKEN,
                response: token,
                tokenId: token?.id,
            });
        } catch (error) {
            state.application.connector.emitter.emit("checkout:three-ds-authorization-failure", {
                error,
                resourceType: ResourceType.TRANSACTION_TOKEN,
                response: authToken,
                tokenId: authToken?.id,
            });

            throw error;
        }

        return token;
    })();

    return validToken;
};

export const updateTokenCvvAndThreeDsData = async (
    { token, data }: { token: TransactionTokenItem; data: FormCheckoutData },
    state: StateShape
) => {
    if (token.paymentType !== PaymentType.CARD) {
        return token;
    }

    const ADDRESS_KEY_SET = new Set(["line1", "line2", "state", "city", "country", "zip"]);

    // Note: Currently only patching token for card payments
    const billingData = (token?.data as TransactionTokenCardDataItem)?.billing || {};

    const { phoneNumber, line1, line2, state: addressState, city, country, zip } = (data?.data ||
        {}) as TransactionTokenCardData;

    const hasEditedPhoneNumber =
        !!phoneNumber?.localNumber && !!phoneNumber?.countryCode && !isEqual(phoneNumber, billingData.phoneNumber);
    const hasEditedAddress = Object.keys(data?.data || {}).some(
        (key) =>
            ADDRESS_KEY_SET.has(key) &&
            (data.data[key] || key === "line2") &&
            data.data[key] !== billingData[key] &&
            (!!data.data[key] || !!billingData[key])
    );

    const threeDsRequired = isTokenThreeDsRequired(state.application.params?.params, state.configuration.data, token);
    const threeDsAddressRequired = isTransactionThreeDsAddressRequired(
        state.application.params?.params,
        state.configuration.data
    );

    const cvv = (data as FormDataCard).data.cvv;

    return (threeDsAddressRequired && hasEditedAddress) || (threeDsRequired && hasEditedPhoneNumber) || cvv
        ? sdk.transactionTokens.update(token.storeId, token.id, {
              data: {
                  ...(cvv && { cvv }),
                  ...(hasEditedAddress && { line1, line2, state: addressState, city, country, zip }),
                  ...(hasEditedPhoneNumber && { phoneNumber }),
              },
          } as PatchedTransactionTokenUpdateParams)
        : token;
};

export const updateCallCenterToken = async (
    callCenterStoreId: string,
    callCenterTokenId: string,
    checkoutData: FormCheckoutData
) => {
    const cardCheckoutData = checkoutData as { paymentType: PaymentType } & FormDataCard & CheckoutPaymentData;
    const { data } = cardCheckoutData || {};
    const { cvv, phoneNumber, cardNumber } = (data || {}) as CardData;

    // In case the card number is present, a new token will be created, therefore we should not update the cvv
    const shouldUpdateCvv = cvv && !cardNumber;
    const hasPhoneNumber = phoneNumber?.localNumber && phoneNumber?.countryCode;

    // Do not update the email as we do not have the secret
    return hasPhoneNumber || shouldUpdateCvv
        ? sdk.transactionTokens.update(callCenterStoreId, callCenterTokenId, {
              data: { ...(hasPhoneNumber ? { phoneNumber } : {}), ...(shouldUpdateCvv && { cvv }) },
          } as PatchedTransactionTokenUpdateParams)
        : sdk.transactionTokens.get(callCenterStoreId, callCenterTokenId); // Get token for return type consistency
};

export const isTransactionThreeDsRequired = (
    params: CheckoutParams,
    checkoutInfoData: PatchedCheckoutInfo,

    /**
     * @deprecated Hack to patch an API bug where the address and phone number are required for 3DS even when skipped
     *
     * In the short term we need to display the phone number and address even when skipped. To be removed once the API has a fix.
     */
    ignoreThreeDsSkip = true
) =>
    (ignoreThreeDsSkip || params.threeDsMode !== ThreeDsMode.SKIP) &&
    (checkoutInfoData?.cardConfiguration?.threeDsRequired ||
        [ThreeDsMode.FORCE, ThreeDsMode.REQUIRE, ThreeDsMode.IF_AVAILABLE].includes(params.threeDsMode));

export const isTokenThreeDsRequired = (
    params: CheckoutParams,
    checkoutInfoData: PatchedCheckoutInfo,
    token: TransactionTokenItem,
    options?: { ignoreThreeDsSkip?: boolean }
) =>
    isTransactionThreeDsRequired(params, checkoutInfoData, options?.ignoreThreeDsSkip) &&
    token.type === TransactionTokenType.RECURRING &&
    token.paymentType === PaymentType.CARD;

export const isTransactionThreeDsAddressRequired = (
    params: CheckoutParams,
    checkoutInfoData: PatchedCheckoutInfo,
    ignoreThreeDsSkip?: boolean
) =>
    isTransactionThreeDsRequired(params, checkoutInfoData, ignoreThreeDsSkip) &&
    checkoutInfoData?.cardConfiguration?.threeDsAddressRequired;

export const isTransactionThreeDsPhoneNumberRequired = (
    params: CheckoutParams,
    checkoutInfoData: PatchedCheckoutInfo,
    ignoreThreeDsSkip?: boolean
) =>
    isTransactionThreeDsRequired(params, checkoutInfoData, ignoreThreeDsSkip) &&
    checkoutInfoData?.cardConfiguration?.threeDsPhoneNumberRequired;
